import React, { useEffect, useState } from 'react';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiArrowRight,
  FiBriefcase,
  FiCheck,
  FiClock,
  FiCreditCard,
  FiDollarSign,
  FiFilter,
  FiPackage,
  FiRefreshCw,
  FiRotateCcw,
  FiSearch,
  FiX,
} from 'react-icons/fi';
import { api } from '../services/api';
import {
  formatTransactionAmount,
  formatTransactionDate,
  PAYMENT_METHOD_LABELS,
  transactionModeLabel,
  transactionStatusInfo,
} from '../utils/paymentTransactions';
import './AdminTransactions.css';

const EMPTY_FILTERS = {
  q: '',
  status: 'all',
  type: 'all',
  method: 'all',
  from: '',
  to: '',
};

const STATUS_OPTIONS = [
  ['all', 'همه وضعیت‌ها'],
  ['succeeded', 'موفق'],
  ['initiated', 'در حال ایجاد'],
  ['pending', 'در انتظار نتیجه'],
  ['failed', 'ناموفق'],
  ['expired', 'منقضی‌شده'],
  ['refunded', 'بازگشت وجه'],
  ['unknown', 'نامشخص'],
];

const TYPE_OPTIONS = [
  ['all', 'همه سفارش‌ها'],
  ['product', 'محصولات'],
  ['service', 'خدمات'],
];

const METHOD_OPTIONS = [
  ['all', 'همه روش‌ها'],
  ['gateway', 'درگاه بانکی'],
  ['card_to_card', 'کارت‌به‌کارت'],
  ['bank_transfer', 'واریز بانکی'],
  ['cash', 'پرداخت نقدی'],
  ['other', 'پرداخت دستی'],
];

const defaultSummary = {
  total: 0,
  succeeded: 0,
  failed: 0,
  pending: 0,
  paidAmount: 0,
  paidByCurrency: [],
  refunded: 0,
  refundedAmount: 0,
  refundedByCurrency: [],
};

const formatCurrencyGroups = (groups, fallbackAmount) => (
  Array.isArray(groups) && groups.length
    ? groups.map((item) => `${formatTransactionAmount(item.amount)} ${item.currency}`).join(' / ')
    : `${formatTransactionAmount(fallbackAmount)} تومان`
);

const transactionNumber = (transaction) => (
  transaction.gatewayInvoiceId != null
    ? String(transaction.gatewayInvoiceId)
    : String(transaction.id || '').slice(-12)
);

const AdminTransactions = ({ onCountChange, onOpenOrder }) => {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(defaultSummary);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 1,
    total: 0,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const requestFilters = {
      ...appliedFilters,
      from: appliedFilters.from
        ? new Date(`${appliedFilters.from}T00:00:00`).toISOString()
        : '',
      to: appliedFilters.to
        ? new Date(`${appliedFilters.to}T23:59:59.999`).toISOString()
        : '',
    };
    const params = new URLSearchParams({
      ...requestFilters,
      page: String(page),
      limit: '20',
    });

    api.get(`/api/payment/admin/transactions?${params.toString()}`)
      .then((response) => {
        if (!active) return;
        setTransactions(Array.isArray(response.data) ? response.data : []);
        setSummary(response.summary || defaultSummary);
        setPagination(response.pagination || { page: 1, pageCount: 1, total: 0 });
        onCountChange?.(Number(response.totals?.total || 0));
      })
      .catch((requestError) => {
        if (!active) return;
        setTransactions([]);
        setError(
          requestError.data?.message
          || requestError.message
          || 'دریافت تراکنش‌ها انجام نشد'
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [appliedFilters, onCountChange, page, refreshKey]);

  const applyFilters = (event) => {
    event.preventDefault();
    if (filters.from && filters.to && filters.from > filters.to) {
      setError('تاریخ شروع نمی‌تواند بعد از تاریخ پایان باشد.');
      return;
    }
    setError('');
    setPage(1);
    setAppliedFilters({ ...filters, q: filters.q.trim() });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
    setRefreshKey((value) => value + 1);
  };

  return (
    <section className="admin-transactions">
      <header className="admin-transactions-heading">
        <div>
          <span><FiCreditCard /></span>
          <div>
            <h2>تراکنش‌ها</h2>
            <p>گزارش یکپارچه پرداخت‌های بانکی و دستی محصولات و خدمات</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setRefreshKey((value) => value + 1)}
          disabled={loading}
        >
          <FiRefreshCw /> {loading ? 'در حال دریافت...' : 'به‌روزرسانی'}
        </button>
      </header>

      <div className="admin-transactions-summary">
        <article className="is-total">
          <span><FiCreditCard /></span>
          <div>
            <small>تعداد تراکنش‌ها</small>
            <strong>{Number(summary.total || 0).toLocaleString('fa-IR')}</strong>
          </div>
        </article>
        <article className="is-income">
          <span><FiDollarSign /></span>
          <div>
            <small>مجموع دریافتی موفق</small>
            <strong>{formatCurrencyGroups(summary.paidByCurrency, summary.paidAmount)}</strong>
          </div>
        </article>
        <article className="is-success">
          <span><FiCheck /></span>
          <div>
            <small>پرداخت موفق</small>
            <strong>{Number(summary.succeeded || 0).toLocaleString('fa-IR')}</strong>
          </div>
        </article>
        <article className="is-pending">
          <span><FiClock /></span>
          <div>
            <small>در انتظار نتیجه</small>
            <strong>{Number(summary.pending || 0).toLocaleString('fa-IR')}</strong>
          </div>
        </article>
        <article className="is-refund">
          <span><FiRotateCcw /></span>
          <div>
            <small>بازگشت وجه</small>
            <strong>{formatCurrencyGroups(summary.refundedByCurrency, summary.refundedAmount)}</strong>
          </div>
        </article>
      </div>

      <form className="admin-transactions-filters" onSubmit={applyFilters}>
        <div className="admin-transactions-search">
          <FiSearch />
          <input
            value={filters.q}
            onChange={(event) => setFilters({ ...filters, q: event.target.value })}
            placeholder="مشتری، ایمیل، موبایل، شماره سفارش یا پیگیری..."
          />
        </div>
        <label>
          <span>وضعیت</span>
          <select
            value={filters.status}
            onChange={(event) => setFilters({ ...filters, status: event.target.value })}
          >
            {STATUS_OPTIONS.map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>نوع سفارش</span>
          <select
            value={filters.type}
            onChange={(event) => setFilters({ ...filters, type: event.target.value })}
          >
            {TYPE_OPTIONS.map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>روش پرداخت</span>
          <select
            value={filters.method}
            onChange={(event) => setFilters({ ...filters, method: event.target.value })}
          >
            {METHOD_OPTIONS.map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>از تاریخ</span>
          <input
            type="date"
            value={filters.from}
            onChange={(event) => setFilters({ ...filters, from: event.target.value })}
          />
        </label>
        <label>
          <span>تا تاریخ</span>
          <input
            type="date"
            value={filters.to}
            onChange={(event) => setFilters({ ...filters, to: event.target.value })}
          />
        </label>
        <div className="admin-transactions-filter-actions">
          <button type="submit"><FiFilter /> اعمال فیلتر</button>
          <button type="button" onClick={resetFilters}><FiX /> پاک‌کردن</button>
        </div>
      </form>

      {error && (
        <div className="admin-transactions-error" role="alert">
          <FiAlertCircle />
          <span>{error}</span>
          <button type="button" onClick={() => setRefreshKey((value) => value + 1)}>
            تلاش دوباره
          </button>
        </div>
      )}

      <div className="admin-transactions-table-wrap">
        <table className="admin-transactions-table">
          <thead>
            <tr>
              <th>تراکنش</th>
              <th>سفارش</th>
              <th>مشتری</th>
              <th>نوع</th>
              <th>مبلغ</th>
              <th>روش پرداخت</th>
              <th>وضعیت</th>
              <th>تاریخ</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="admin-transactions-empty">
                  <span className="admin-transactions-loader" /> در حال دریافت تراکنش‌ها...
                </td>
              </tr>
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan="9" className="admin-transactions-empty">
                  تراکنشی مطابق این فیلترها یافت نشد.
                </td>
              </tr>
            ) : transactions.map((transaction) => {
              const statusInfo = transactionStatusInfo(transaction.status);
              const reference = transaction.manualReference || transaction.gatewayReference;
              return (
                <tr key={transaction.id}>
                  <td>
                    <strong>#{transactionNumber(transaction)}</strong>
                    <small>{transaction.source === 'manual' ? 'ثبت دستی' : 'درگاه سپهر'}</small>
                  </td>
                  <td>
                    <strong>#{transaction.orderNumber || '—'}</strong>
                    <small>{transaction.title || 'بدون عنوان'}</small>
                  </td>
                  <td>
                    <strong>{transaction.customer?.name || 'کاربر نامشخص'}</strong>
                    <small>
                      {transaction.customer?.phone || transaction.customer?.email || 'اطلاعات تماس ثبت نشده'}
                    </small>
                  </td>
                  <td>
                    <span className={`admin-transaction-type is-${transaction.orderType}`}>
                      {transaction.orderType === 'service' ? <FiBriefcase /> : <FiPackage />}
                      {transaction.orderType === 'service' ? 'خدمات' : 'محصول'}
                    </span>
                  </td>
                  <td>
                    <strong className="admin-transaction-amount">
                      {formatTransactionAmount(transaction.amount)}
                    </strong>
                    <small>{transaction.currency || 'تومان'}</small>
                  </td>
                  <td>
                    <strong>
                      {PAYMENT_METHOD_LABELS[transaction.paymentMethod]
                        || PAYMENT_METHOD_LABELS.other}
                    </strong>
                    <small>
                      {transaction.source === 'manual'
                        ? 'پرداخت تأییدشده خارج از سایت'
                        : transactionModeLabel(transaction)}
                      {reference ? ` · پیگیری ${reference}` : ''}
                    </small>
                    {transaction.source === 'manual' && transaction.recordedBy?.name && (
                      <small>ثبت توسط {transaction.recordedBy.name}</small>
                    )}
                  </td>
                  <td>
                    <span className={`admin-transaction-status is-${statusInfo.tone}`}>
                      {statusInfo.label}
                    </span>
                    {transaction.failureMessage && (
                      <small className="is-error">{transaction.failureMessage}</small>
                    )}
                  </td>
                  <td>
                    <span>{formatTransactionDate(
                      transaction.completedAt || transaction.requestedAt
                    )}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="admin-transaction-open"
                      onClick={() => onOpenOrder?.(transaction)}
                      disabled={!transaction.orderId}
                    >
                      مشاهده سفارش <FiArrowLeft />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination.pageCount > 1 && (
        <nav className="admin-transactions-pagination" aria-label="صفحه‌بندی تراکنش‌ها">
          <button
            type="button"
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            disabled={pagination.page <= 1 || loading}
          >
            <FiArrowRight /> جدیدتر
          </button>
          <span>
            صفحه {Number(pagination.page || 1).toLocaleString('fa-IR')}
            {' '}از {Number(pagination.pageCount || 1).toLocaleString('fa-IR')}
            {' · '}
            {Number(pagination.total || 0).toLocaleString('fa-IR')} نتیجه
          </span>
          <button
            type="button"
            onClick={() => setPage((value) => Math.min(pagination.pageCount, value + 1))}
            disabled={pagination.page >= pagination.pageCount || loading}
          >
            قدیمی‌تر <FiArrowLeft />
          </button>
        </nav>
      )}
    </section>
  );
};

export default AdminTransactions;
